import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { leaveAPI, companyAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Loader2, Calendar, CheckCircle2, AlertCircle, Clock, XCircle } from 'lucide-react';

const MyHolidays = () => {
    const { user } = useAuth();
    const [holidays, setHolidays] = useState([]);
    const [allowedOptionalHolidays, setAllowedOptionalHolidays] = useState(0);
    const [requestedHolidays, setRequestedHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submittingId, setSubmittingId] = useState(null);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    useEffect(() => {
        fetchData();
    }, [selectedYear]);

    const fetchData = async () => {
        try {
            const [holidaysRes, companyRes, reqRes] = await Promise.all([
                leaveAPI.getHolidays(selectedYear),
                companyAPI.getCurrent(),
                leaveAPI.getHolidayRequests({ employee_id: user?.employee_id, my_requests_only: true })
            ]);
            setHolidays(holidaysRes.data);
            setAllowedOptionalHolidays(companyRes.data.allowed_optional_holidays || 0);
            setRequestedHolidays(reqRes.data);
        } catch (error) {
            toast.error('Failed to fetch holidays and preferences');
        } finally {
            setLoading(false);
        }
    };

    const handleRequestHoliday = async (holidayId) => {
        setSubmittingId(holidayId);
        try {
            await leaveAPI.createHolidayRequest({ holiday_id: holidayId });
            toast.success('Optional holiday request submitted successfully');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to request holiday');
        } finally {
            setSubmittingId(null);
        }
    };

    const handleCancelHoliday = async (requestId, holidayId) => {
        setSubmittingId(holidayId);
        try {
            await leaveAPI.cancelHolidayRequest(requestId);
            toast.success('Optional holiday request cancelled');
            fetchData();
        } catch (error) {
            toast.error('Failed to cancel request');
        } finally {
            setSubmittingId(null);
        }
    };

    // Calculate optional holiday stats for selected year
    const activeRequests = requestedHolidays.filter(r => 
        r.holiday_date && 
        r.holiday_date.startsWith(selectedYear) && 
        ['PENDING', 'APPROVED'].includes(r.status)
    );
    const usedCount = activeRequests.length;
    const remainingCount = Math.max(0, allowedOptionalHolidays - usedCount);

    // Get upcoming holidays (holidays that are today or in the future)
    const upcomingHolidays = holidays.filter(holiday => {
        const holidayDate = new Date(holiday.date);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return holidayDate >= today;
    });

    const getRequestStatusBadge = (req) => {
        if (!req) return null;
        switch (req.status) {
            case 'APPROVED':
                return <Badge className="bg-green-100 text-green-800 hover:bg-green-100 flex gap-1 items-center w-fit"><CheckCircle2 className="h-3 w-3" /> Approved</Badge>;
            case 'PENDING':
                return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 flex gap-1 items-center w-fit"><Clock className="h-3 w-3" /> Pending</Badge>;
            case 'REJECTED':
                return <Badge className="bg-red-100 text-red-800 hover:bg-red-100 flex gap-1 items-center w-fit"><XCircle className="h-3 w-3" /> Rejected</Badge>;
            case 'CANCELLED':
                return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100 flex gap-1 items-center w-fit">Cancelled</Badge>;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-slide-up">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Company Holidays</h1>
                    <p className="text-muted-foreground text-sm sm:text-base">View company holiday calendar and request optional holidays</p>
                </div>
                <div className="flex gap-2">
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                        className="px-3 py-2 border rounded-md"
                    >
                        {[2023, 2024, 2025, 2026].map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Optional Holidays Stats Card */}
            {allowedOptionalHolidays > 0 && (
                <Card className="border-orange-200 bg-orange-50/30">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-orange-800 text-lg flex items-center gap-2">
                            <AlertCircle className="h-5 w-5 text-orange-600" />
                            Optional Holidays Info ({selectedYear})
                        </CardTitle>
                        <CardDescription className="text-orange-700/80">
                            You are allowed to take up to {allowedOptionalHolidays} optional holidays from the list provided by the admin.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm">
                                <p className="text-xs text-muted-foreground font-medium uppercase">Allowed</p>
                                <p className="text-2xl font-bold text-slate-800">{allowedOptionalHolidays}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm">
                                <p className="text-xs text-muted-foreground font-medium uppercase">Used / Pending</p>
                                <p className="text-2xl font-bold text-orange-700">{usedCount}</p>
                            </div>
                            <div className="bg-white p-3 rounded-lg border border-orange-100 shadow-sm">
                                <p className="text-xs text-muted-foreground font-medium uppercase">Remaining</p>
                                <p className="text-2xl font-bold text-green-700">{remainingCount}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Upcoming Holidays */}
            {upcomingHolidays.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-primary" />
                            Upcoming Holidays
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {upcomingHolidays.map((holiday) => {
                                const date = new Date(holiday.date);
                                const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                                const isToday = date.toDateString() === new Date().toDateString();
                                const req = requestedHolidays.find(r => r.holiday_id === holiday.id && r.status !== 'CANCELLED');

                                return (
                                    <div
                                        key={holiday.id}
                                        className={`p-4 rounded-lg border flex flex-col justify-between min-h-[160px] ${isToday ? 'border-primary bg-primary/5' : ''}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <h3 className="font-semibold text-slate-800">{holiday.name}</h3>
                                                <p className="text-xs text-muted-foreground">{dayName}</p>
                                                <p className="text-sm font-semibold text-primary mt-1">
                                                    {date.toLocaleDateString('en-US', {
                                                        month: 'long',
                                                        day: 'numeric',
                                                        year: 'numeric'
                                                    })}
                                                </p>
                                                {holiday.description && (
                                                    <p className="text-xs text-muted-foreground mt-1.5">{holiday.description}</p>
                                                )}
                                            </div>
                                            <div className="flex flex-col gap-1 items-end">
                                                <Badge variant={holiday.is_optional ? 'secondary' : 'default'}>
                                                    {holiday.is_optional ? 'Optional' : 'Mandatory'}
                                                </Badge>
                                                {isToday && (
                                                    <Badge variant="outline" className="text-primary border-primary">
                                                        Today
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>

                                        {holiday.is_optional && (
                                            <div className="mt-4 pt-3 border-t flex items-center justify-between">
                                                <div className="text-xs text-slate-500 font-medium">
                                                    {req ? getRequestStatusBadge(req) : "Not Requested"}
                                                </div>
                                                {req ? (
                                                    ['PENDING', 'APPROVED'].includes(req.status) && (
                                                        <Button
                                                            size="xs"
                                                            variant="outline"
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 text-[11px] h-7 px-2"
                                                            onClick={() => handleCancelHoliday(req.id, holiday.id)}
                                                            disabled={submittingId === holiday.id}
                                                        >
                                                            {submittingId === holiday.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                                            Cancel
                                                        </Button>
                                                    )
                                                ) : (
                                                    <Button
                                                        size="xs"
                                                        className="text-[11px] h-7 px-2.5 bg-orange-600 hover:bg-orange-700 text-white"
                                                        onClick={() => handleRequestHoliday(holiday.id)}
                                                        disabled={submittingId === holiday.id || remainingCount <= 0 || allowedOptionalHolidays === 0}
                                                    >
                                                        {submittingId === holiday.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                                        Apply
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* All Holidays Table */}
            <Card>
                <CardHeader>
                    <CardTitle>All Holidays {selectedYear}</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Day</TableHead>
                                <TableHead>Holiday Name</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead>Type</TableHead>
                                <TableHead>Request Status</TableHead>
                                <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {holidays.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                                        No holidays found for {selectedYear}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                holidays.map((holiday) => {
                                    const date = new Date(holiday.date);
                                    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
                                    const today = new Date();
                                    today.setHours(0, 0, 0, 0);
                                    const hasPassed = date < today;
                                    const req = requestedHolidays.find(r => r.holiday_id === holiday.id && r.status !== 'CANCELLED');

                                    return (
                                        <TableRow key={holiday.id} className={hasPassed ? 'opacity-50' : ''}>
                                            <TableCell>{date.toLocaleDateString()}</TableCell>
                                            <TableCell>{dayName}</TableCell>
                                            <TableCell className="font-medium">{holiday.name}</TableCell>
                                            <TableCell>{holiday.description || '-'}</TableCell>
                                            <TableCell>
                                                <Badge variant={holiday.is_optional ? 'secondary' : 'default'}>
                                                    {holiday.is_optional ? 'Optional' : 'Mandatory'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {holiday.is_optional ? (
                                                    req ? getRequestStatusBadge(req) : <span className="text-slate-400 text-xs">Not Requested</span>
                                                ) : (
                                                    <span className="text-slate-400 text-xs">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {holiday.is_optional && !hasPassed ? (
                                                    req ? (
                                                        ['PENDING', 'APPROVED'].includes(req.status) && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs h-8"
                                                                onClick={() => handleCancelHoliday(req.id, holiday.id)}
                                                                disabled={submittingId === holiday.id}
                                                            >
                                                                {submittingId === holiday.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                                                Cancel
                                                            </Button>
                                                        )
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            className="text-xs h-8 bg-orange-600 hover:bg-orange-700 text-white"
                                                            onClick={() => handleRequestHoliday(holiday.id)}
                                                            disabled={submittingId === holiday.id || remainingCount <= 0 || allowedOptionalHolidays === 0}
                                                        >
                                                            {submittingId === holiday.id ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                                            Apply
                                                        </Button>
                                                    )
                                                ) : null}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        </div>
    );
};

export default MyHolidays;
